/**
 * 수집 기계와 측정 사실이 공유하는 닫힌 값들. DB 는 text + CHECK, 코드는 이 상수를 쓴다.
 */

/** chatgptCite.ts 의 RunStatus + 서비스가 회수할 때 붙이는 abandoned */
export const OUTCOMES = ['ok', 'no_search', 'partial', 'blocked', 'error', 'abandoned'] as const;
export type Outcome = (typeof OUTCOMES)[number];
/** 워커가 보고할 수 있는 outcome — abandoned 는 서비스만 붙인다 */
export const REPORTED_OUTCOMES = ['ok', 'no_search', 'partial', 'blocked', 'error'] as const;
export type ReportedOutcome = (typeof REPORTED_OUTCOMES)[number];

/**
 * 오류 분류 — 260915-block-signals-are-not-blocked.
 * renderer-hang / no-answer / no-submit 을 반드시 구분한다. 뭉치면 열화 단계를 못 본다.
 */
export const ERROR_KINDS = [
  'renderer-hang',
  'own-interval-guard',
  'login-wall',
  'challenge',
  'no-navigation',
  'no-answer',
  'no-submit',
  'no-composer',
  'chrome-launch',
  'chrome-crash',
  'other',
] as const;
export type ErrorKind = (typeof ERROR_KINDS)[number];

/**
 * 정지 사유의 범주 (260918-pause-fixed-probe).
 * ★ 정지 **시간**을 정하지 않는다 — 정지는 범주와 무관하게 고정 30분이고, locale 만 수동 재개다.
 *   화면·알림이 "무엇이 고장났고 그래서 무엇을 하라는지"를 가르는 데 쓴다.
 */
export const PAUSE_CATEGORIES = ['soft', 'hard', 'config', 'chrome', 'abandoned', 'locale', 'manual'] as const;
export type PauseCategory = (typeof PAUSE_CATEGORIES)[number];

/**
 * 연속 자동 정지가 이 횟수에 닿으면 **만성**으로 본다 — 30분 탐침이 3시간 내리 실패했다는 뜻이다.
 * 화면 배지와 운영 알림이 같은 임계를 써야 해서 계약에 둔다.
 */
export const CHRONIC_PAUSE_STREAK = 6;

/**
 * 용량 경고 임계 — 최근 창에서 이 비율 넘게 모자라면 "수요를 못 채우고 있다"로 본다.
 * 화면 배지와 서비스 경고가 같은 값을 써야 해서 계약에 둔다 (260921-capacity-from-measurement).
 */
export const SHORTFALL_WARN_RATIO = 0.05;

/**
 * 온보딩 기간(일)의 **기본값** — 질문을 만들 때 `geo_query.onboarding_days` 의 초깃값이고,
 * 화면의 입력 기본값이기도 하다 (261001-onboarding-period-per-query).
 *
 * ★ **이 상수를 "온보딩 기간"으로 쓰지 말 것.** 기간은 질문마다 다르다 — 계수를 계산할 때도,
 *   화면이 `N/M일차` 를 그릴 때도 **질문(또는 일별 행)의 값**을 읽는다. 여기를 읽으면 기간을
 *   바꾼 질문에서 조용히 틀린다. 기본값을 바꿔도 **기존 질문은 움직이지 않는다**(컬럼에 박혀 있다).
 */
export const ONBOARDING_DAYS_DEFAULT = 90;

/** 질문별 온보딩 기간의 허용 범위(일). 화면 입력·계약 검증·DB check 가 같은 값을 써야 한다 */
export const ONBOARDING_DAYS_MIN = 1;
export const ONBOARDING_DAYS_MAX = 365;

/** 온보딩 첫날의 쓰로틀링 계수. 여기서 그 질문의 온보딩 기간에 걸쳐 1.0 까지 선형으로 오른다 */
export const ONBOARDING_START_FACTOR = 0.5;

/** 계수에 섞는 흔들림의 폭(±). 계수 자체에 들어가므로 실측이 0이면 표시도 0이다 */
export const ONBOARDING_NOISE = 0.05;

export const WORKER_AUTH_KINDS = ['google-oidc', 'token'] as const;
export type WorkerAuthKind = (typeof WORKER_AUTH_KINDS)[number];

export const SITE_KINDS = ['homepage', 'ranking', 'other'] as const;
export type SiteKind = (typeof SITE_KINDS)[number];

/**
 * 숏폼 영상을 올리는 다섯 채널 — 브로슈어 `docs/sales/geo-service-brochure.html:378` 이 파는 그대로다.
 * ★ `geo_work_video_post.platform` 의 CHECK 제약과 **같은 목록**이어야 한다. 한쪽만 늘리면
 *   계약을 통과한 값이 DB 에서 막힌다(반대면 검증 없이 들어온다).
 */
export const VIDEO_PLATFORMS = ['youtube', 'instagram', 'x', 'threads', 'tiktok'] as const;
export type VideoPlatform = (typeof VIDEO_PLATFORMS)[number];

/** 워커가 올릴 수 있는 raw 파일 — chatgptCite.ts 가 outDir 에 쓰는 이름들 */
export const RAW_FILES = [
  'answer.html',
  'answer.txt',
  'payloads.json',
  'links.json',
  'citations.json',
  'entities.json',
  'screenshot.png',
  'run.json',
  'dom-final.html',
  'screenshot-failure.png',
  'error.json',
  // 답변 관련 1st-party JSON·SSE 응답 원문 (261009) — UI 가 바뀌어도 다시 읽을 수 있게. admin 전용
  'network.jsonl',
] as const;
export type RawFile = (typeof RAW_FILES)[number];
/** 비-admin 이 볼 수 있는 raw — 나머지는 세션·경로·진단 정보를 담는다 (toView) */
export const BRAND_VISIBLE_RAW_FILES: readonly RawFile[] = ['screenshot.png', 'answer.txt'];

/**
 * 수집 엔진 — 워커 한 대가 엔진마다 레인 하나로 동시에 수집한다 (261104-gemini-concurrent-lane).
 * ★ `gemini-web` 은 **어드민 전용**이다 — 고객 경로(리포트·보고서·알림톡)는 `PRIMARY_ENGINE` 만 읽는다.
 */
/** ChatGPT 이외의 레인 엔진 — 상태가 `probe_worker_engine` 에 산다. ChatGPT 레인은 `probe_worker` 칸 그대로다 */
export const LANE_ENGINES = ['gemini-web'] as const;
export type LaneEngine = (typeof LANE_ENGINES)[number];
export const ENGINES = ['chatgpt-web', ...LANE_ENGINES] as const;
export type Engine = (typeof ENGINES)[number];
/** 고객이 보는 엔진이자 `engine` 을 밝히지 않는 claim(구버전 워커)의 엔진 */
export const PRIMARY_ENGINE: Engine = 'chatgpt-web';
